import { useEffect, useMemo, useRef, useState } from 'react';

// The normal player and its local-file retry both used ExoPlayer. Use the
// MediaPlayer implementation already shipped in expo-av for the downloaded
// retry, so an ExoPlayer failure does not strand an otherwise valid recording.
export function useAndroidReadAloudPlayer(sourceUrl, onError) {
  const errorRef = useRef(onError);
  errorRef.current = onError;
  const [status, setStatus] = useState({ isLoaded: false, playing: false });
  const state = useMemo(() => ({ active: false, intent: 0, volume: 1, rate: 1,
    position: 0, duration: 0, ready: null, sound: null, report: null }), [sourceUrl]);

  const player = useMemo(() => {
    const report = error => state.report?.(error);
    // Child layout/player effects can issue controls before our load effect.
    const command = action => Promise.resolve().then(() => state.ready).then(() => {
      if (state.active) return action(state.sound);
    });
    return {
      get currentTime() { return state.position; },
      get duration() { return state.duration; },
      set volume(value) {
        state.volume = value;
        command(sound => sound.setVolumeAsync(value)).catch(report);
      },
      setPlaybackRate(value) {
        state.rate = value;
        command(sound => sound.setRateAsync(value, true)).catch(report);
      },
      async configureAudioMode(background) {
        const { Audio, InterruptionModeAndroid } = require('expo-av');
        await Audio.setAudioModeAsync({ staysActiveInBackground: background,
          interruptionModeAndroid: InterruptionModeAndroid.DoNotMix,
          shouldDuckAndroid: false, playThroughEarpieceAndroid: false });
      },
      play() {
        const intent = ++state.intent;
        return command(sound => {
          if (intent === state.intent) return sound.playAsync();
        });
      },
      pause() {
        ++state.intent;
        command(sound => sound.pauseAsync()).catch(report);
      },
      seekTo(seconds) {
        return command(sound => sound.setPositionAsync(seconds * 1000));
      },
    };
  }, [state]);

  useEffect(() => {
    const { Audio } = require('expo-av');
    state.active = true;
    const sound = new Audio.Sound();
    state.sound = sound;
    let failed = false;
    let disposed = false;
    const report = error => {
      if (disposed || !state.active || failed) return;
      failed = true;
      errorRef.current?.(error);
    };
    state.report = report;
    const update = value => {
      if (disposed || !state.active) return;
      if (value.error) { report(new Error(value.error)); return; }
      state.position = (value.positionMillis || 0) / 1000;
      state.duration = (value.durationMillis || 0) / 1000;
      setStatus({ isLoaded: value.isLoaded, playing: !!value.isPlaying,
        currentTime: state.position, duration: state.duration,
        didJustFinish: !!value.didJustFinish });
    };
    sound.setOnPlaybackStatusUpdate(update);
    state.ready = sound.loadAsync({ uri: sourceUrl }, {
      androidImplementation: 'MediaPlayer', shouldPlay: false,
      volume: state.volume, rate: state.rate, shouldCorrectPitch: true,
      progressUpdateIntervalMillis: 500,
    }, false).then(update);
    state.ready.catch(report);
    return () => {
      disposed = true;
      state.active = false;
      ++state.intent;
      sound.setOnPlaybackStatusUpdate(null);
      // Expo cannot unload while loadAsync is pending. Release it as soon as
      // that settles, even if Stop/unmount happened before loading finished.
      state.ready.catch(() => {}).then(() => sound.unloadAsync()).catch(() => {});
    };
  }, [sourceUrl, state]);

  return { player, status };
}
